import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Search, PackageSearch, Loader2 } from "lucide-react";
import { nwgStats, nwgParts, nwgReimport, euro } from "@/lib/store";

const PT_LABEL = { DISPLAY: "Display", TOUCH: "Touch", BACK_COVER: "Back Cover", BATTERIA: "Batteria",
  CONNETTORE: "Connettore", CAMERA: "Fotocamera", SPEAKER: "Speaker", FLEX: "Flex", MICROFONO: "Microfono", ALTRO: "Altro" };
const QCLASS_CLS = { ORIGINALE: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  COMPATIBILE: "bg-blue-500/15 text-blue-400 border-blue-500/30" };

export default function NWGLists() {
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [pt, setPt] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [], page: 1, page_size: 30 });
  const [loading, setLoading] = useState(false);
  const [reimporting, setReimporting] = useState(false);

  const loadStats = useCallback(() => { nwgStats().then(setStats).catch(() => {}); }, []);
  const load = useCallback(() => {
    setLoading(true);
    nwgParts({ q, part_type: pt, page, page_size: 30 })
      .then(setData).catch(() => toast.error("Errore nel caricamento ricambi"))
      .finally(() => setLoading(false));
  }, [q, pt, page]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);

  const doReimport = async () => {
    setReimporting(true);
    try {
      const r = await nwgReimport();
      toast.success("Listini NewWaveGames re-importati", { description: `${r.total} ricambi aggiornati` });
      loadStats(); setPage(1); load();
    } catch (e) { toast.error("Errore re-import", { description: e.response?.data?.detail || "" }); }
    finally { setReimporting(false); }
  };

  const pages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 30)));
  const ptOptions = stats ? Object.keys(stats.by_part_type || {}).filter((k) => k !== "null" && k !== "ALTRO") : [];

  return (
    <div className="space-y-6" data-testid="nwg-lists-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-white"><PackageSearch className="h-5 w-5 text-[#2563EB]" /> Listini NewWaveGames</h2>
          <p className="text-sm text-zinc-500">Ricambi fornitore prioritario · usati dal Customer AI per i preventivi</p>
        </div>
        <button data-testid="nwg-reimport-btn" onClick={doReimport} disabled={reimporting}
          className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {reimporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-importa PDF
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-xl border border-white/10 bg-[#11141A] p-4"><div className="text-2xl font-bold text-white" data-testid="nwg-total">{stats.total}</div><div className="mt-1 text-xs text-zinc-500">Ricambi totali</div></div>
          {Object.entries(stats.by_part_type || {}).filter(([k]) => k !== "null" && k !== "ALTRO").map(([k, v]) => (
            <div key={k} className="rounded-xl border border-white/10 bg-[#11141A] p-4"><div className="text-2xl font-bold text-white">{v}</div><div className="mt-1 text-xs text-zinc-500">{PT_LABEL[k] || k}</div></div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <input data-testid="nwg-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Cerca modello o descrizione (es. iPhone 12, Galaxy S21…)"
            className="w-full rounded-lg border border-white/10 bg-[#0B0E13] py-2 pl-9 pr-3 text-sm text-white" />
        </div>
        <select data-testid="nwg-part-type" value={pt} onChange={(e) => { setPt(e.target.value); setPage(1); }}
          className="rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-sm text-white">
          <option value="">Tutti i tipi</option>
          {ptOptions.map((k) => <option key={k} value={k}>{PT_LABEL[k] || k}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-[#11141A] text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-4 py-3">Descrizione</th><th className="px-4 py-3">Modello/i</th>
              <th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Qualità</th>
              <th className="px-4 py-3 text-right">Costo fornitore</th><th className="px-4 py-3 text-right">Montaggio</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Nessun ricambio trovato</td></tr>
            ) : data.items.map((it, i) => (
              <tr key={i} data-testid={`nwg-row-${i}`} className="text-zinc-200">
                <td className="px-4 py-3">{it.raw_description || it.model}</td>
                <td className="px-4 py-3 text-zinc-400">{(it.models || []).join(" · ")}</td>
                <td className="px-4 py-3">{PT_LABEL[it.part_type] || it.part_type || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${QCLASS_CLS[it.quality_class] || "border-white/10 text-zinc-400"}`}>
                    {it.quality_class === "ORIGINALE" ? "Originale" : "Compatibile"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-medium text-white">{euro(it.cost_cents)}</td>
                <td className="px-4 py-3 text-right text-zinc-400">{it.installation_cost_cents ? euro(it.installation_cost_cents) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-zinc-400">
        <span data-testid="nwg-count">{data.total} ricambi</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-white/10 px-3 py-1.5 disabled:opacity-40">← Prec</button>
          <span>{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-white/10 px-3 py-1.5 disabled:opacity-40">Succ →</button>
        </div>
      </div>
      <p className="text-xs text-zinc-600">Il costo fornitore è visibile solo qui in dashboard e non viene mai mostrato al cliente: il preventivo usa il prezzo calcolato dal Pricing Engine IPHIX.</p>
    </div>
  );
}
